import { useDrawerInvokers } from "@tiny/lib";
import React from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { v4 as uuid } from "uuid";
import { useWorkflowsBuilderPageView } from "../../pages/WorkflowsBuilderPage/WorkflowsBuilderPage.context";
import { useGetWorkflowSplitStatsQuery } from "../../app/useGeneratedApi";
import {
  SplitBranch,
  SplitData,
  SplitGoal,
  SplitWinnerSelection,
} from "../../pages/WorkflowsBuilderPage/WorkflowsBuilder/types";
import {
  applySplitBranches,
  buildSplitData,
  createDefaultSplitData,
  getSplitTotal,
  MAX_SPLIT_BRANCHES,
  MIN_SPLIT_BRANCHES,
  nextBranchId,
  validateSplitSettings,
} from "../../pages/WorkflowsBuilderPage/WorkflowsBuilder/Split/split.helpers";

/** Number inputs hold "" while empty. */
export interface TrafficSplitFormValues {
  branches: { id: string; name: string; percentage: number | "" }[];
  goal: SplitGoal | "";
  winnerSelection: SplitWinnerSelection;
  testDurationHours: number | "";
}

const toFormValues = (data: SplitData): TrafficSplitFormValues => {
  const defaults = createDefaultSplitData();
  return {
    branches: (data.branches?.length
      ? data.branches
      : (defaults.branches as SplitBranch[])
    ).map((branch) => ({ ...branch })),
    goal: data.goal || "",
    winnerSelection: data.winnerSelection || "MANUAL",
    testDurationHours: data.testDurationHours ?? "",
  };
};

const toNumber = (value: number | "") => (value === "" ? NaN : value);

export const toSplitData = (values: TrafficSplitFormValues): SplitData =>
  buildSplitData({
    branches: (values.branches || []).map((branch) => ({
      id: branch.id,
      name: branch.name || "",
      percentage: toNumber(branch.percentage),
    })),
    goal: values.goal || undefined,
    winnerSelection: values.winnerSelection,
    testDurationHours:
      values.testDurationHours === ""
        ? undefined
        : toNumber(values.testDurationHours),
  });

const useTrafficSplitForm = ({ id }: { id: string }) => {
  const {
    getNode,
    viewMode,
    workflowId,
    workflowData,
    setGraphData,
    setSplitPreview,
    showNodeStats,
  } = useWorkflowsBuilderPageView();
  const { hideDrawer } = useDrawerInvokers();
  const node = getNode(id);
  const initialValues = React.useMemo(
    () => toFormValues(node?.data || {}),
    // Only the node as it was when the panel opened.
    []
  );

  // The published split, to tell whether a switch to lifetime clears a winner.
  // Before the first publish it 412s, which means there is no winner to clear.
  const { data: publishedSplit } = useGetWorkflowSplitStatsQuery(
    workflowId,
    id,
    {
      enabled: showNodeStats && initialValues.winnerSelection !== "LIFETIME",
      onError: () => {},
    }
  );
  const [pendingLifetimeData, setPendingLifetimeData] =
    React.useState<SplitData | null>(null);

  const methods = useForm<TrafficSplitFormValues>({
    defaultValues: initialValues,
    mode: "onChange",
  });
  const { control, handleSubmit } = methods;
  // keyName keeps react-hook-form's own key off the branch's "id".
  const { fields, append, remove } = useFieldArray({
    control,
    name: "branches",
    keyName: "fieldKey",
  });

  const formValues = useWatch({ control }) as TrafficSplitFormValues;
  const splitData = toSplitData(formValues);
  const errors = validateSplitSettings(splitData);
  const total = getSplitTotal(splitData.branches);
  const isUnchanged =
    JSON.stringify(formValues) === JSON.stringify(initialValues);

  React.useEffect(() => {
    setSplitPreview({ nodeId: id, data: splitData });
    // splitData is rebuilt every render; formValues is what changes.
  }, [JSON.stringify(formValues)]);

  React.useEffect(() => () => setSplitPreview(null), []);

  const addBranch = () => {
    const branches = (formValues.branches || []) as SplitBranch[];
    if (branches.length >= MAX_SPLIT_BRANCHES) return;
    const branchId = nextBranchId(branches);
    append({
      id: branchId,
      name: `Path ${branchId.toUpperCase()}`,
      percentage: "",
    });
  };

  const removeBranch = (index: number) => {
    if ((formValues.branches || []).length <= MIN_SPLIT_BRANCHES) return;
    remove(index);
  };

  const goBack = () => {
    setSplitPreview(null);
    hideDrawer();
  };

  const applySplitData = (data: SplitData) => {
    const { nodes, edges } = workflowData.structure;
    const updatedNodes = nodes.map((current) =>
      current.id === id
        ? {
            ...current,
            // Replaced wholesale so a duration left over from AUTOMATIC is not sent.
            data: {
              label: current.data.label,
              ...data,
              config: { configured: true },
            },
          }
        : current
    );
    setGraphData(
      applySplitBranches(
        { nodes: updatedNodes, edges },
        id,
        data.branches as SplitBranch[],
        uuid
      )
    );
    setSplitPreview(null);
    hideDrawer();
  };

  const onSubmit = handleSubmit((values) => {
    const data = toSplitData(values);
    if (validateSplitSettings(data).length > 0) return;
    if (
      data.winnerSelection === "LIFETIME" &&
      initialValues.winnerSelection !== "LIFETIME" &&
      publishedSplit?.winner
    ) {
      setPendingLifetimeData(data);
      return;
    }
    applySplitData(data);
  });

  const confirmLifetimeSwitch = () => {
    if (pendingLifetimeData) applySplitData(pendingLifetimeData);
    setPendingLifetimeData(null);
  };

  const cancelLifetimeSwitch = () => setPendingLifetimeData(null);

  return {
    control,
    methods,
    fields,
    formValues,
    errors,
    total,
    addBranch,
    removeBranch,
    canAddBranch: fields.length < MAX_SPLIT_BRANCHES,
    canRemoveBranch: fields.length > MIN_SPLIT_BRANCHES,
    saveDisabled: viewMode || isUnchanged || errors.length > 0,
    goBack,
    onSubmit,
    viewMode,
    lifetimeConfirmOpen: !!pendingLifetimeData,
    confirmLifetimeSwitch,
    cancelLifetimeSwitch,
  };
};

export default useTrafficSplitForm;
